// Features/Reservation/Hooks/useParticipants.ts

import { useCallback, useMemo } from 'react';

/**
 * Hook for managing participants in a reservation.
 * Provides functions for adding participants, button disabling logic, and participant count state.
 */
export function useParticipants({
  formData,
  setFormData,
  applicantById,
  today,
  maxParticipants = 5,
  applicantLoading = false,
}: {
  formData: any;
  setFormData: (fn: (prev: any) => any) => void;
  applicantById: any;
  today: string;
  maxParticipants?: number;
  applicantLoading?: boolean;
}) {
  const handleAddEmptyParticipant = useCallback(() => {
    if (formData.participants.length >= maxParticipants) return;
    setFormData((prev) => ({
      ...prev,
      participants: [
        ...prev.participants,
        {
          id: 0,
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          birthDate: '',
          address: '',
          city: '',
          zipCode: '',
        },
      ],
    }));
  }, [formData.participants.length, maxParticipants, setFormData]);

  const handleAddParticipantFromApplicant = useCallback(() => {
    if (formData.applicantId && formData.applicantId !== '__new__') {
      if (applicantById && String(applicantById.id) === String(formData.applicantId)) {
        setFormData((prev) => ({
          ...prev,
          participants: [
            ...prev.participants,
            {
              id: 0,
              firstName: '',
              lastName: applicantById.lastName || '',
              email: '',
              phoneNumber: '',
              birthDate: today,
              address: applicantById.street || '',
              city: applicantById.city || '',
              zipCode: applicantById.zipCode || '',
            },
          ],
        }));
      }

      return;
    }

    const sourceApplicant = formData.applicant;
    if (!sourceApplicant) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      participants: [
        ...prev.participants,
        {
          id: 0,
          firstName: '',
          lastName: sourceApplicant.lastName || '',
          email: '',
          phoneNumber: '',
          birthDate: today,
          address: sourceApplicant.street || '',
          city: sourceApplicant.city || '',
          zipCode: sourceApplicant.zipCode || '',
        },
      ],
    }));
  }, [formData, setFormData, applicantById, today]);

  const maxReached = formData.participants.length >= maxParticipants;

  const disabledFromApplicant = useMemo(
    () =>
      (!formData.applicantId && !formData.applicant?.firstName) || maxReached || applicantLoading,
    [formData.applicantId, formData.applicant?.firstName, maxReached, applicantLoading]
  );

  return {
    handleAddEmptyParticipant,
    handleAddParticipantFromApplicant,
    maxReached,
    disabledFromApplicant,
    participants: formData.participants,
  };
}
